class UpsellProduct {
  constructor(container) {
    this.container = container;
    this.productsData = this.getProductsData();
    this.currentProductId = container.dataset.productId;
    this.cartItems = new Set();
    this.cartUpdateUnsubscriber = null;
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.init();
  }

  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  init() {
    if (!this.productsData) return;
    
    this.container.addEventListener('change', this.handleColorChange.bind(this));
    this.container.addEventListener('submit', this.handleFormSubmit.bind(this));
    this.loadCartState();
    this.setupCartChangeListeners();
  }

  getProductsData() {
    const dataElement = document.querySelector('[data-upsell-products]');
    return dataElement ? JSON.parse(dataElement.textContent) : null;
  }

  setupCartChangeListeners() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'upsell-product') return;
        this.handleCartUpdate(event);
      });
    }
  }

  async handleCartUpdate(event = null) {
    if (event?.cartData) {
      this.syncCartStateFromData(event.cartData);
    } else {
      await this.syncCartState();
    }
    
    const currentProduct = this.productsData.products[this.currentProductId];
    if (currentProduct) {
      this.updateForm(currentProduct);
    }
  }

  syncCartStateFromData(cartData) {
    const currentProduct = this.productsData.products[this.currentProductId];
    if (!currentProduct) return;
    
    const currentVariantId = currentProduct.selected_or_first_available_variant.id.toString();
    const isInCart = cartData.items?.some(item => {
      const itemVariantId = item.variant_id?.toString() || item.id?.toString();
      return itemVariantId === currentVariantId;
    }) || false;
    
    if (isInCart) {
      this.cartItems.add(this.currentProductId);
    } else {
      this.cartItems.delete(this.currentProductId);
    }
    
    this.updateForm(currentProduct);
  }

  async syncCartState() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.syncCartStateFromData(cart);
    } catch (error) {
      console.error('Error syncing cart state:', error);
    }
  }

  async loadCartState() {
    await this.syncCartState();
    const currentProduct = this.productsData.products[this.currentProductId];
    if (currentProduct) {
      this.updateForm(currentProduct);
    }
  }

  isProductInCart(product) {
    return this.cartItems.has(product.id.toString());
  }

  addToCartState(productId, variantId) {
    this.cartItems.add(productId);
  }

  handleFormSubmit(event) {
    const form = event.target;
    if (!form.matches('.upsell-product__form')) return;
    
    event.preventDefault();
    
    const variantInput = form.querySelector('.upsell-variant-id');
    const variantId = variantInput?.value;
    const currentProduct = this.productsData.products[this.currentProductId];
    
    if (variantId && currentProduct) {
      this.addProductToCart(variantId, currentProduct);
    }
  }

  async addProductToCart(variantId, product) {
    try {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      
      if (this.cart) {
        formData.append(
          'sections',
          this.cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }
      
      config.body = formData;
      const response = await fetch(routes.cart_add_url, config);
      const cartData = await response.json();

      if (cartData.status) {
        console.error('Error adding to cart:', cartData.description);
        return;
      }

      this.addToCartState(this.currentProductId, variantId);
      this.updateFormToCartState(product);

      const fullCartResponse = await fetch('/cart.js');
      const fullCartData = await fullCartResponse.json();

      if (this.cart) {
        const renderData = {
          ...fullCartData,
          sections: cartData.sections
        };
        
        this.renderCartSections(renderData);
        this.reattachOverlayListener();
        this.cart.close();
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'upsell-product',
          productVariantId: variantId,
          cartData: fullCartData
        });
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  renderCartSections(cartData) {
    const sectionsToRender = this.cart.getSectionsToRender();
    
    sectionsToRender.forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      
      if (section.id === 'cart-drawer' || section.section === 'cart-drawer') {
        const cartDrawerWrapper = sectionElement.closest('cart-drawer') || document.querySelector('cart-drawer');
        if (cartDrawerWrapper) {
          const isEmpty = (cartData.items?.length || cartData.item_count || 0) === 0;
          cartDrawerWrapper.classList.toggle('is-empty', isEmpty);
        }
      }
      
      if (cartData.sections) {
        const sectionKey = section.section || section.id;
        if (cartData.sections[sectionKey]) {
          const newHTML = this.getSectionInnerHTML(cartData.sections[sectionKey], section.selector);
          sectionElement.innerHTML = newHTML;
        }
      }
    });
  }

  reattachOverlayListener() {
    setTimeout(() => {
      const overlay = this.cart.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.replaceWith(overlay.cloneNode(true));
        this.cart.querySelector('#CartDrawer-Overlay')
          .addEventListener('click', this.cart.close.bind(this.cart));
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleColorChange(event) {
    if (!event.target.matches('input[data-color-option]')) return;
    
    const selectedColor = event.target.dataset.colorOption;
    const productId = this.getProductIdForColor(selectedColor);
    
    if (productId && productId !== this.currentProductId) {
      this.switchToProduct(productId);
      this.currentProductId = productId;
    }
    
    this.updateVisualState();
  }

  getProductIdForColor(color) {
    const colorMap = this.productsData.colorToProductMap;
    const mappings = colorMap.split(';');
    
    for (const mapping of mappings) {
      const [mappedColor, productId] = mapping.split('|');
      if (mappedColor === color) {
        return productId;
      }
    }
    
    return null;
  }

  switchToProduct(productId) {
    const product = this.productsData.products[productId];
    if (!product) return;

    this.currentProductId = productId;
    this.container.dataset.productId = productId;

    this.updateImage(product);
    this.updateTitle(product);
    this.updatePrice(product);
    this.checkCurrentProductInCart();
  }

  async checkCurrentProductInCart() {
    await this.syncCartState();
  }

  updateImage(product) {
    const imageContainer = this.container.querySelector('[data-dynamic-content="image"]');
    if (imageContainer?.querySelector('img') && product.featured_image) {
      const img = imageContainer.querySelector('img');
      img.src = product.featured_image.url;
      img.alt = product.featured_image.alt;
      img.srcset = `${product.featured_image.url} 120w`;
    }
  }

  updateTitle(product) {
    const titleElement = this.container.querySelector('[data-dynamic-content="title"]');
    if (titleElement) {
      titleElement.textContent = product.title;
    }
  }

  updatePrice(product) {
    const priceContainer = this.container.querySelector('[data-dynamic-content="price"]');
    if (!priceContainer || !product.selected_or_first_available_variant) return;
    
    const price = this.formatPrice(product.selected_or_first_available_variant.price);
    const compareAtPrice = product.compare_at_price;
    const isOnSale = compareAtPrice && compareAtPrice > product.selected_or_first_available_variant.price;
    
    const priceClasses = `price upsell-price${isOnSale ? ' price--on-sale' : ''}`;
    let priceHtml = `<div class="${priceClasses}"><div class="price__container">`;
    
    if (isOnSale) {
      const comparePrice = this.formatPrice(compareAtPrice);
      priceHtml += `
        <div class="price__regular" style="display: none;">
          <span class="visually-hidden visually-hidden--inline">Regular price</span>
          <span class="price-item price-item--regular">${price}</span>
        </div>
        <div class="price__sale">
          <span class="visually-hidden visually-hidden--inline">Sale price</span>
          <span class="price-item price-item--sale price-item--last">${price}</span>
          <span class="visually-hidden visually-hidden--inline">Regular price</span>
          <span><s class="price-item price-item--regular">${comparePrice}</s></span>
        </div>`;
    } else {
      priceHtml += `
        <div class="price__regular">
          <span class="visually-hidden visually-hidden--inline">Regular price</span>
          <span class="price-item price-item--regular">${price}</span>
        </div>
        <div class="price__sale" style="display: none;">
          <span class="visually-hidden visually-hidden--inline">Sale price</span>
          <span class="price-item price-item--sale price-item--last">${price}</span>
        </div>`;
    }
    
    priceHtml += '</div></div>';
    priceContainer.innerHTML = priceHtml;
  }

  updateForm(product) {
    const formContainer = this.container.querySelector('[data-dynamic-content="form"]');
    if (!formContainer) return;
    
    const isAvailable = product.available && product.selected_or_first_available_variant && product.selected_or_first_available_variant.available !== false;
    const isInCart = this.cartItems.has(product.id.toString());
    
    if (isInCart) {
      this.updateFormToCartState(product);
    } else if (isAvailable) {
      this.updateFormToAddState(product);
    } else {
      this.updateFormToDisabledState(product);
    }
  }

  updateFormToAddState(product) {
    const formContainer = this.container.querySelector('[data-dynamic-content="form"]');
    const plusIcon = this.productsData.plusIcon || '';
    
    formContainer.innerHTML = `
      <form action="/cart/add" method="post" enctype="multipart/form-data" class="upsell-product__form" data-type="add-to-cart-form">
        <input type="hidden" name="id" value="${product.selected_or_first_available_variant.id}" class="upsell-variant-id">
        <button type="submit" class="upsell-product__add-button" aria-label="Add to cart">
          <span class="svg-wrapper">
            ${plusIcon}
          </span>
        </button>
      </form>
    `;
  }

  updateFormToCartState(product) {
    const formContainer = this.container.querySelector('[data-dynamic-content="form"]');
    const tickIcon = this.productsData.tickIcon || '';
    
    formContainer.innerHTML = `
      <div class="upsell-product__form">
        <button type="button" class="upsell-product__add-button upsell-product__add-button--in-cart" disabled aria-label="In cart">
          <span class="svg-wrapper">
            ${tickIcon}
          </span>
        </button>
      </div>
    `;
  }

  updateFormToDisabledState(product) {
    const formContainer = this.container.querySelector('[data-dynamic-content="form"]');
    const plusIcon = this.productsData.plusIcon || '';
    
    formContainer.innerHTML = `
      <div class="upsell-product__form">
        <button type="button" class="upsell-product__add-button upsell-product__add-button--disabled" disabled aria-label="Unavailable">
          <span class="svg-wrapper">
            ${plusIcon}
          </span>
        </button>
      </div>
    `;
  }

  formatPrice(priceInCents) {
    return `€${(priceInCents / 100).toFixed(2)}`;
  }

  updateVisualState() {
    const allOptions = this.container.querySelectorAll('.upsell-product__color-option');
    const checkedInputs = this.container.querySelectorAll('input[type="radio"]:checked');
    
    allOptions.forEach((option) => option.classList.remove('upsell-product__color-option--selected'));
    checkedInputs.forEach((input) => {
      input.closest('.upsell-product__color-option')?.classList.add('upsell-product__color-option--selected');
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.upsell-product').forEach((container) => {
    new UpsellProduct(container);
  });
});
